import _ from 'lodash';
import { MarketsEnum } from 'src/api/models/Enums';
import { isMarket } from 'src/utils/markets';
import type { MotornetModelSummary } from '../types';

/** Campi periodo condivisi da modelli, versioni e match autosuggest. */
type ProductionPeriod = {
	da?: string | null;
	a?: string | null;
	inizioProduzione?: string | null;
	fineProduzione?: string | null;
};

/** `YYYY-MM` (o `YYYY-MM-DD`) → anno e mese. */
const yearMonthOf = (
	value: string | null | undefined
): { year: number; month: number } | null => {
	const match = /^(\d{4})-(\d{1,2})/.exec((value ?? '').trim());
	return match ? { year: Number(match[1]), month: Number(match[2]) } : null;
};

const shortYearMonth = (value: string | null | undefined): string | null => {
	const ym = yearMonthOf(value);
	return ym
		? `${String(ym.year).slice(2)}/${String(ym.month).padStart(2, '0')}`
		: null;
};

/** `20/03-25/06`, oppure `20/03-current` se la produzione e' ancora aperta. */
export const productionRangeLabel = (period: ProductionPeriod): string => {
	const start = shortYearMonth(period.inizioProduzione ?? period.da);
	if (!start) return '';
	const end = shortYearMonth(period.fineProduzione ?? period.a);
	return `${start}-${end ?? 'current'}`;
};

/**
 * Su ES il modello si riconosce dalla descrizione estesa piu' il periodo di
 * produzione — due gamme omonime differiscono solo per quello. Su IT resta
 * `codice - descrizione`.
 */
export const modelOptionLabel = (model: MotornetModelSummary): string => {
	const code = model.gammaModello?.codice ?? '';
	const descr = model.gammaModello?.descrizione ?? '';

	if (!isMarket(MarketsEnum.es)) {
		return [code, _.startCase(_.toLower(descr))].filter(Boolean).join(' - ');
	}

	const name = model.modello || descr || code;
	const range = productionRangeLabel(model);
	return range ? `${name} ${range}` : name;
};

/**
 * Anno/mese con cui interrogare `getMotornetVehicle`: su ES e' l'inizio
 * produzione — della versione se Autovista lo popola, altrimenti del modello —
 * che seleziona il periodo di listino. Su IT il veicolo si chiede senza data,
 * come prima.
 */
export const productionFetchParams = (
	...sources: (ProductionPeriod | null | undefined)[]
): { year?: number; month?: number } => {
	if (!isMarket(MarketsEnum.es)) return {};
	for (const source of sources) {
		const ym = source && yearMonthOf(source.inizioProduzione ?? source.da);
		if (ym) return { year: ym.year, month: ym.month };
	}
	return {};
};
